import React, { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Dialog, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarsIcon from '@mui/icons-material/Stars';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { ICampaignHostPackage } from '../../../types/global/campaignHostSubscription.types';
import { findLtdPackage, formatTierPackagePrice } from '../../../utils/campaignHostSubscriptionFormat';
import { LTD_TIER_FEATURE_ROWS, getSuiteFeature } from './campaignHostTierFeatures';
import { CampaignHostLtdPromoCountdown } from './CampaignHostLtdPromoCountdown';
import { fetchCampaignHostPricing } from '../../../redux/campaignHostSubscription/actions';
import { BRAND_LUXE_PRIMARY, BRAND_LUXE_SECONDARY, BRAND_LUXE_BG } from '../brandDashboardTokens';

const PROMO_MODAL_Z_INDEX = 1600;

export interface CampaignHostLtdPromoModalProps {
    open: boolean;
    onClose: () => void;
    packages: ICampaignHostPackage[];
    promoPriceCents: number | null;
    promoEndsAt?: string | null;
    ltdPromoActive?: boolean;
    onClaimLtd: (pkg: ICampaignHostPackage) => void;
    onViewAllPlans?: () => void;
}

export function CampaignHostLtdPromoModal({
    open,
    onClose,
    packages,
    promoPriceCents,
    promoEndsAt = null,
    ltdPromoActive = false,
    onClaimLtd,
    onViewAllPlans,
}: CampaignHostLtdPromoModalProps) {
    const dispatch = useDispatch();
    const muiTheme = useTheme();
    const fullScreen = useMediaQuery(muiTheme.breakpoints.down('md'));

    const handleCountdownComplete = useCallback(() => {
        dispatch(fetchCampaignHostPricing());
        onClose();
    }, [dispatch, onClose]);

    const ltdPackage = useMemo(() => findLtdPackage(packages), [packages]);
    const displayPrice = useMemo(() => {
        if (!ltdPackage) return '—';
        return formatTierPackagePrice(ltdPackage, { ltdPromoActive, promoPriceCents });
    }, [ltdPackage, ltdPromoActive, promoPriceCents]);

    const benefitIcons: Record<string, React.ElementType> = {
        unlimited: RocketLaunchIcon,
        saveFees: SavingsOutlinedIcon,
        priority: TrendingUpIcon,
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: PROMO_MODAL_Z_INDEX }}
            slotProps={{
                backdrop: { sx: { zIndex: PROMO_MODAL_Z_INDEX - 1 } },
            }}
            PaperProps={{
                sx: {
                    zIndex: PROMO_MODAL_Z_INDEX,
                    bgcolor: BRAND_LUXE_BG,
                    backgroundImage: 'none',
                    border: `1px solid ${BRAND_LUXE_PRIMARY}22`,
                    borderRadius: fullScreen ? 0 : 4,
                    overflow: 'hidden',
                    maxWidth: 920,
                    boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
                },
            }}>
            <IconButton
                onClick={onClose}
                aria-label="Close"
                sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2, color: '#adaaaa' }}>
                <CloseIcon />
            </IconButton>

            <Box
                sx={{
                    position: 'relative',
                    textAlign: 'center',
                    p: { xs: 4, md: 6 },
                    pt: { xs: 6, md: 7 },
                    background: `linear-gradient(160deg, ${BRAND_LUXE_BG} 0%, #1a1510 50%, ${BRAND_LUXE_BG} 100%)`,
                }}>
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(circle at 50% 0%, ${BRAND_LUXE_PRIMARY}18 0%, transparent 55%)`,
                        pointerEvents: 'none',
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 720, mx: 'auto' }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 0.75,
                            mb: 3,
                            borderRadius: 99,
                            bgcolor: 'rgba(0,108,80,0.4)',
                            border: `1px solid ${BRAND_LUXE_SECONDARY}44`,
                        }}>
                        <StarsIcon sx={{ fontSize: 16, color: BRAND_LUXE_SECONDARY }} />
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: BRAND_LUXE_SECONDARY,
                                letterSpacing: '0.15em',
                            }}>
                            LIMITED TIME OFFER
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <CampaignHostLtdPromoCountdown
                            endsAt={promoEndsAt}
                            show={ltdPromoActive}
                            onComplete={handleCountdownComplete}
                        />
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 900,
                            fontSize: { xs: 40, md: 64 },
                            lineHeight: 1,
                            letterSpacing: '-0.04em',
                            mb: 2,
                        }}>
                        <Box component="span" sx={{ display: 'block', color: '#fff' }}>
                            THE FOREVER
                        </Box>
                        <Box component="span" sx={{ display: 'block', color: BRAND_LUXE_PRIMARY }}>
                            PASS
                        </Box>
                    </Typography>

                    <Typography sx={{ color: '#adaaaa', fontSize: { xs: 16, md: 18 }, mb: 4, lineHeight: 1.6 }}>
                        Secure unlimited campaign creation with one payment. No monthly fees.
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: 12,
                            letterSpacing: '0.2em',
                            color: '#adaaaa',
                            textTransform: 'uppercase',
                            mb: 1,
                        }}>
                        One-Time Payment
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 900,
                            fontSize: { xs: 48, md: 64 },
                            color: '#fff',
                            mb: 4,
                        }}>
                        {displayPrice}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                            gap: 2,
                            mb: 4,
                            textAlign: 'left',
                        }}>
                        {LTD_TIER_FEATURE_ROWS.map(row => {
                            const meta = getSuiteFeature(row.key);
                            const Icon = benefitIcons[row.key] ?? RocketLaunchIcon;
                            return (
                                <Box
                                    key={row.key}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        bgcolor: 'rgba(26,25,25,0.5)',
                                        border: `1px solid ${BRAND_LUXE_PRIMARY}18`,
                                    }}>
                                    <Icon sx={{ color: BRAND_LUXE_PRIMARY, mb: 1 }} />
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: '#fff',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        }}>
                                        {meta.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: '#adaaaa', mt: 0.75, lineHeight: 1.45 }}>
                                        {meta.description}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Button
                        disabled={!ltdPackage}
                        onClick={() => ltdPackage && onClaimLtd(ltdPackage)}
                        sx={{
                            py: 2,
                            px: 6,
                            borderRadius: 99,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 800,
                            fontSize: 18,
                            textTransform: 'none',
                            color: '#392700',
                            background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY} 0%, #feb700 100%)`,
                            boxShadow: '0 0 30px rgba(255,201,101,0.4)',
                        }}>
                        Claim Lifetime Deal
                    </Button>
                    <Typography sx={{ mt: 2, fontSize: 11, color: '#666', letterSpacing: '0.12em' }}>
                        SECURE STRIPE CHECKOUT • INSTANT ACCESS
                    </Typography>
                    {onViewAllPlans ? (
                        <Button
                            type="button"
                            onClick={onViewAllPlans}
                            sx={{
                                mt: 3,
                                color: '#adaaaa',
                                textTransform: 'none',
                                fontSize: 14,
                                '&:hover': { color: BRAND_LUXE_PRIMARY, bgcolor: 'transparent' },
                            }}>
                            Compare monthly & lifetime plans
                        </Button>
                    ) : null}
                </Box>
            </Box>
        </Dialog>
    );
}
